import { useId, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import type { PanelSpan } from '@/components/shared';
import { useInterviewLink } from '../useInterviewLink';
import type { BackofficeProjectDetail } from '../types';
import { ProjectDetailPanel } from './ProjectDetailPanelGrid';
import { InterviewLinkValue } from './InterviewLinkValue';
import { EditInterviewLinkModal } from './EditInterviewLinkModal';

export function ProjectInterviewLinkPanel({
  project,
  currentSpan,
  onUpdated,
}: {
  project: BackofficeProjectDetail;
  currentSpan: PanelSpan;
  onUpdated: () => void;
}) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  const link = useInterviewLink(project.id);
  const hasAlternativeHostname = link.data?.options.some(
    (option) => option.hostname !== link.data.hostname
  );
  const noAlternativeHostname =
    link.isSuccess && !!link.data.url && link.data.canChangeHostname && !hasAlternativeHostname;
  const canEdit = !project.deletedAt && link.data?.canChangeHostname && hasAlternativeHostname;
  return (
    <>
      <ProjectDetailPanel
        className="[&_[data-tile-header-actions]]:opacity-100 [&_.tile-drag-header_h2]:pr-10"
        title="Interview link"
        panelId="study-interview-link"
        span="2x1"
        currentSpan={currentSpan}
        headerActions={
          !project.deletedAt ? (
            <span
              className="group/interview-link-settings focus-ring relative inline-flex rounded-md"
              tabIndex={noAlternativeHostname ? 0 : undefined}
              aria-label={noAlternativeHostname ? 'Interview link settings' : undefined}
              aria-describedby={noAlternativeHostname ? tooltipId : undefined}
            >
              <button
                type="button"
                aria-label="Interview link settings"
                aria-describedby={noAlternativeHostname ? tooltipId : undefined}
                title={noAlternativeHostname ? undefined : 'Interview link settings'}
                disabled={!canEdit}
                onClick={() => setOpen(true)}
                className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 hover:bg-backoffice-50 hover:text-neutral-700 disabled:pointer-events-none disabled:opacity-40"
              >
                <SlidersHorizontal className="size-4" aria-hidden="true" />
              </button>
              {noAlternativeHostname ? (
                <span
                  id={tooltipId}
                  role="tooltip"
                  className="pointer-events-none absolute right-0 top-full z-20 mt-1 hidden w-56 max-w-[calc(100vw-3rem)] rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white group-hover/interview-link-settings:block group-focus-within/interview-link-settings:block"
                >
                  No alternative interview hostname is available.
                </span>
              ) : null}
            </span>
          ) : undefined
        }
      >
        <div className="flex h-full items-center">
          <InterviewLinkValue projectId={project.id} />
        </div>
      </ProjectDetailPanel>
      {open && link.data ? (
        <EditInterviewLinkModal
          link={link.data}
          onClose={() => setOpen(false)}
          onSave={async (hostname) => {
            await link.update.mutateAsync(hostname);
            onUpdated();
          }}
        />
      ) : null}
    </>
  );
}
